import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin, Facebook, Instagram, ArrowRight } from 'lucide-react';

const Footer = () => {
  return (
    <footer className="bg-[#1a1a1a] text-gray-300">
      {/* Top CTA strip */}
      <div className="bg-orange-500">
        <div className="max-w-7xl mx-auto px-4 py-6 flex flex-col md:flex-row items-center justify-between gap-4">
          <div>
            <h3 className="text-white text-lg font-bold">Besoin d'un devis gratuit ?</h3>
            <p className="text-orange-100 text-sm">Nos conseillers vous répondent sous 24h</p>
          </div>
          <Link
            to="/contact"
            className="flex items-center gap-2 bg-white text-orange-600 font-bold px-6 py-3 rounded-lg hover:bg-orange-50 transition-colors shadow-md"
          >
            Demander un devis gratuit
            <ArrowRight size={16} />
          </Link>
        </div>
      </div>

      {/* Main Footer */}
      <div className="max-w-7xl mx-auto px-4 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
          {/* Brand */}
          <div className="lg:col-span-1">
            <div className="mb-4">
              <img
                src="/images/logo-banner.jpg"
                alt="Stores & Pergolas"
                className="h-12 w-auto object-contain brightness-0 invert mb-2"
                onError={(e) => { e.target.style.display = 'none'; }}
              />
              <p className="text-[10px] tracking-widest text-gray-500 uppercase mt-1">Fabricant Français • Depuis 1986</p>
            </div>
            <p className="text-gray-400 text-sm leading-relaxed mb-5">
              Spécialiste et fabricant français de stores et de toiles sur-mesure depuis 1986. Notre usine de 2000m² est
              située à Gardanne dans les Bouches du Rhône (13).
            </p>
            <div className="flex items-center gap-3">
              <a href="#" className="w-9 h-9 rounded-full bg-gray-800 flex items-center justify-center hover:bg-orange-500 transition-colors">
                <Facebook size={16} />
              </a>
              <a href="#" className="w-9 h-9 rounded-full bg-gray-800 flex items-center justify-center hover:bg-orange-500 transition-colors">
                <Instagram size={16} />
              </a>
            </div>
          </div>

          {/* Nos stores */}
          <div>
            <h4 className="text-white font-semibold mb-5 text-sm uppercase tracking-wider">Nos Stores</h4>
            <ul className="space-y-2.5">
              {[
                { label: 'Store Banne Monobloc', href: '/produit/model-m-store-banne-monobloc' },
                { label: 'Store Banne Premier Prix', href: '/produit/model-e-store-banne-premier-prix' },
                { label: 'Store de Véranda', href: '/produit/model-v-store-veranda' },
                { label: 'Store à Projection', href: '/produit/model-i-store-projection' },
                { label: 'Store Banne Coffre', href: '/produit/model-c-store-banne-coffre' },
                { label: 'Store de Terrasse', href: '/produit/model-m-store-terrasse' },
              ].map((item) => (
                <li key={item.href}>
                  <Link
                    to={item.href}
                    className="text-sm text-gray-400 hover:text-orange-400 transition-colors flex items-center gap-1.5 group"
                  >
                    <ArrowRight size={12} className="opacity-0 group-hover:opacity-100 transition-opacity text-orange-400" />
                    {item.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Informations */}
          <div>
            <h4 className="text-white font-semibold mb-5 text-sm uppercase tracking-wider">Informations</h4>
            <ul className="space-y-2.5">
              {[
                { label: 'Qui sommes-nous ?', href: '/qui-sommes-nous' },
                { label: 'Service Après Vente', href: '/service-apres-vente' },
                { label: 'Service Pose', href: '/service-pose' },
                { label: 'Garantie 12 ans', href: '/garantie' },
                { label: 'Glossaire du store', href: '/glossaire' },
                { label: 'Classes de résistance au vent', href: '/resistance-vent' },
                { label: 'Indice de réparabilité', href: '/reparabilite' },
                { label: 'Blog', href: '/blog' },
              ].map((item) => (
                <li key={item.href}>
                  <Link
                    to={item.href}
                    className="text-sm text-gray-400 hover:text-orange-400 transition-colors flex items-center gap-1.5 group"
                  >
                    <ArrowRight size={12} className="opacity-0 group-hover:opacity-100 transition-opacity text-orange-400" />
                    {item.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="text-white font-semibold mb-5 text-sm uppercase tracking-wider">Contact</h4>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <MapPin size={16} className="text-orange-400 mt-0.5 flex-shrink-0" />
                <span className="text-sm text-gray-400">Usine de 2000m²<br />Gardanne, Bouches du Rhône (13)<br />Entre Aix et Marseille</span>
              </li>
              <li className="flex items-center gap-3">
                <Phone size={16} className="text-orange-400 flex-shrink-0" />
                <span className="text-sm text-gray-400">09 70 70 30 38</span>
              </li>
              <li className="flex items-center gap-3">
                <Mail size={16} className="text-orange-400 flex-shrink-0" />
                <span className="text-sm text-gray-400">contact@stores-shop.com</span>
              </li>
            </ul>
            <div className="mt-5 p-3 bg-gray-800 rounded-lg">
              <p className="text-xs text-gray-400 mb-1">Nos partenaires</p>
              <div className="flex items-center gap-3 flex-wrap">
                <span className="text-xs font-bold text-white bg-gray-700 px-2 py-1 rounded">SOMFY®</span>
                <span className="text-xs font-bold text-white bg-gray-700 px-2 py-1 rounded">LLAZA®</span>
                <span className="text-xs font-bold text-white bg-gray-700 px-2 py-1 rounded">DICKSON®</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom bar */}
      <div className="border-t border-gray-800">
        <div className="max-w-7xl mx-auto px-4 py-4 flex flex-col md:flex-row items-center justify-between gap-3 text-xs text-gray-500">
          <p>© {new Date().getFullYear()} Stores &amp; Pergolas™ — Fabricant français de stores depuis 1986</p>
          <div className="flex items-center gap-4">
            <Link to="/cgv" className="hover:text-gray-300 transition-colors">CGV</Link>
            <Link to="/mentions-legales" className="hover:text-gray-300 transition-colors">Mentions légales</Link>
            <Link to="/paiement" className="hover:text-gray-300 transition-colors">Moyens de paiement</Link>
            <Link to="/expeditions" className="hover:text-gray-300 transition-colors">Expéditions</Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
